import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft,
  Award,
  Trophy,
  Star,
  Zap,
  Heart,
  Shield,
  Medal,
  Lock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Badge icon mapping
const BADGE_ICONS = {
  "first_response": Medal,
  "helping_hand": Heart,
  "dedicated_responder": Star,
  "veteran_champion": Trophy,
  "valor_hero": Award,
  "quick_responder": Zap,
  "five_star": Star,
  "training_complete": Shield
};

// All possible badges for display
const ALL_BADGES = [
  { id: "first_response", name: "First Response", description: "Complete your first veteran support session", icon: "🏅", threshold: 1 },
  { id: "helping_hand", name: "Helping Hand", description: "Support 5 veterans", icon: "🤝", threshold: 5 },
  { id: "dedicated_responder", name: "Dedicated Responder", description: "Support 10 veterans", icon: "⭐", threshold: 10 },
  { id: "veteran_champion", name: "Veteran Champion", description: "Support 25 veterans", icon: "🏆", threshold: 25 },
  { id: "valor_hero", name: "VALOR Hero", description: "Support 50 veterans", icon: "🎖️", threshold: 50 },
  { id: "quick_responder", name: "Quick Responder", description: "Average response time under 5 minutes", icon: "⚡", threshold: null },
  { id: "five_star", name: "Five Star", description: "Receive 5 five-star ratings", icon: "🌟", threshold: 5 },
  { id: "training_complete", name: "Certified Volunteer", description: "Complete volunteer training with 80%+ score", icon: "📜", threshold: null }
];

const ResponderBadges = ({ user }) => {
  const navigate = useNavigate();
  const [badgeData, setBadgeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    try {
      const response = await axios.get(`${API}/responder/badges`);
      setBadgeData(response.data);
    } catch (error) {
      console.error("Failed to load badges:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const hasBadge = (badgeId) => {
    return badgeData?.badges?.some(b => b.id === badgeId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="va-blue-bar h-2"></div>
      
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/analytics")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
            <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              My Badges
            </span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Progress Card */}
        <Card className="card-valor mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  {badgeData?.earned_count || 0} of {badgeData?.total_possible || 8}
                </h2>
                <p className="text-muted-foreground">Badges Earned</p>
              </div>
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                <Award className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            <Progress value={badgeData?.progress_percent || 0} className="h-3" />
            <p className="text-sm text-muted-foreground mt-2">
              {badgeData?.progress_percent || 0}% complete
            </p>
          </CardContent>
        </Card>

        {/* Earned Badges */}
        {badgeData?.badges && badgeData.badges.length > 0 && (
          <Card className="card-valor mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Trophy className="w-5 h-5 text-yellow-500" />
                Earned Badges
              </CardTitle>
              <CardDescription>
                Badges you've earned through your volunteer work
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {badgeData.badges.map((badge) => (
                  <div 
                    key={badge.id}
                    className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4 text-center"
                  >
                    <div className="text-4xl mb-2">{badge.icon}</div>
                    <h3 className="font-semibold text-foreground">{badge.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Badges */}
        <Card className="card-valor">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Medal className="w-5 h-5 text-primary" />
              All Badges
            </CardTitle>
            <CardDescription>
              Complete milestones to earn these badges
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              {ALL_BADGES.map((badge) => {
                const earned = hasBadge(badge.id);
                return (
                  <div 
                    key={badge.id}
                    className={`relative border rounded-xl p-4 transition-all ${
                      earned 
                        ? "bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200" 
                        : "bg-muted/30 border-border opacity-60"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`text-3xl ${!earned && "grayscale"}`}>
                        {badge.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-foreground">{badge.name}</h3>
                          {earned && (
                            <span className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{badge.description}</p>
                      </div>
                    </div>
                    {!earned && (
                      <div className="absolute top-2 right-2">
                        <Lock className="w-4 h-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ResponderBadges;
